/**
 * 桥梁地图管理的共享状态存储
 * 使用简单的状态管理模式，而不是完整的Vuex，减少依赖
 */
import Vue from 'vue';
import BridgeEventBus, { BRIDGE_EVENTS } from '@/utils/BridgeEventBus';
import mapUtils from '@/utils/mapUtils';

// 定义初始状态，用于重置
const initialState = {
  // 地图状态
  map: null,
  center: [23.13, 113.27],
  zoom: 12,
  
  // 桥梁数据
  bridges: [],
  currentBridge: null,
  
  // 区域数据
  areas: [],
  currentArea: null,
  selectedArea: null, // 当前选中的区域（用于列表选择，与编辑区域分离）
  
  // 绘制状态
  isDrawing: false,
  drawingCoordinates: [],
  markType: 'polygon', // 默认为多边形，可选值：point, line, rectangle, circle, polygon
  pointIconName: 'el-icon-location', // 点标注默认图标
  
  // 绘制辅助变量
  lastClickLat: 0,
  lastClickLng: 0,
  isEditingArea: false,
  
  // 编辑锁定机制
  editingAreaId: null, // 当前正在编辑的区域ID，用于锁定编辑状态
  isEditingLocked: false, // 是否锁定编辑状态，防止意外切换
  
  // UI状态
  drawerVisible: false,
  loading: false,
  
  // 坐标拾取状态
  isLocationPicking: false,
  
  // 当前标记位置
  currentMarker: null,
  
  // 区域颜色映射（id -> 颜色）
  areaColorMap: {}
};

// 创建可响应的状态对象
const state = Vue.observable({...initialState});

const getters = {
  hasCoordinates: () => {
    return !!state.currentBridge && 
           !!state.currentBridge.longitude && 
           !!state.currentBridge.latitude;
  },
  
  markerPosition: () => {
    if (!state.currentBridge) return null;
    return state.currentBridge.latitude && state.currentBridge.longitude ? 
      [state.currentBridge.latitude, state.currentBridge.longitude] : 
      null;
  },
  
  // 当前是否在坐标拾取状态
  isLocationPicking: () => state.isLocationPicking,
  
  // 获取当前桥梁的坐标，如果存在则返回一个包含经纬度的数组
  currentBridgeLocation: () => {
    return state.currentBridge && state.currentBridge.latitude && state.currentBridge.longitude ? 
        [state.currentBridge.latitude, state.currentBridge.longitude] : 
        null;
  },
  
  // 获取当前标记位置
  currentMarker: () => state.currentMarker,
  
  // 是否正在绘制
  isDrawing: () => state.isDrawing,
  
  // 获取绘制的坐标点
  drawingCoordinates: () => state.drawingCoordinates,
  
  // 是否正在编辑区域
  isEditingArea: () => state.isEditingArea,
  
  // 获取当前选中的区域
  selectedArea: () => state.selectedArea,
  
  // 获取当前编辑的区域ID
  editingAreaId: () => state.editingAreaId,
  
  // 是否处于编辑锁定状态
  isEditingLocked: () => state.isEditingLocked
};

const actions = {
  // 地图相关
  setMap(map) {
    state.map = map;
    BridgeEventBus.$emit(BRIDGE_EVENTS.MAP_READY, map);
  },
  
  setCenter(center) {
    state.center = center;
  },
  
  setZoom(zoom) {
    state.zoom = zoom;
  },
  
  // 桥梁相关
  setBridges(bridges) {
    state.bridges = bridges;
    BridgeEventBus.$emit(BRIDGE_EVENTS.BRIDGE_DATA_LOADED, bridges);
  },
  
  setCurrentBridge(bridge) {
    // 添加防御性代码，确保bridge不为空
    if (!bridge) {
      console.warn('setCurrentBridge: 传入的bridge对象为空，操作被跳过');
      return;
    }
    
    try {
      // 创建一个安全的拷贝，防止原对象中的null和undefined属性
      const safeBridge = {};
      
      // 只复制非null和非undefined的属性
      if (typeof bridge === 'object') {
        Object.keys(bridge).forEach(key => {
          if (bridge[key] !== undefined && bridge[key] !== null) {
            safeBridge[key] = bridge[key];
          }
        });
      }
      
      // 安全地设置状态
      state.currentBridge = safeBridge;
      
      // 只有在bridge对象有效时才发送事件
      if (safeBridge && Object.keys(safeBridge).length > 0) {
        BridgeEventBus.$emit(BRIDGE_EVENTS.BRIDGE_SELECTED, safeBridge);
      }
    } catch (error) {
      console.error('设置当前桥梁时出错:', error);
      // 出错时仍尝试设置状态，但不发送事件
      state.currentBridge = bridge || null;
    }
  },
  
  // 区域相关
  setAreas(areas) {
    state.areas = areas;
    // 更新区域颜色映射
    if (Array.isArray(areas)) {
      // 创建区域ID到颜色的映射
      areas.forEach(area => {
        if (area && area.id && area.color) {
          state.areaColorMap[area.id] = area.color;
        }
        //  else if (area && area.id && area.areaType) {
        //   // 如果没有颜色字段，使用mapUtils中的getAreaColor获取默认颜色
        //   const defaultColor = mapUtils.getAreaColor(area.areaType);
        //   state.areaColorMap[area.id] = defaultColor;
        // }
      });
    }
    BridgeEventBus.$emit(BRIDGE_EVENTS.AREA_DATA_LOADED, areas);
  },


  // 根据区域ID获取颜色
  getColorByAreaId(areaId) {
    return state.areaColorMap[areaId] || '#95a5a6'; // 默认返回灰色
  },

  // 根据区域ID获取颜色
  getColorByAreaInfo(areaId, areaType) {
    return state.areaColorMap[areaId] || mapUtils.getAreaColor(areaType); // 默认返回灰色
  },
  
  // 更新区域颜色
  updateAreaColor(areaId, color) {
    if (areaId && color) {
      state.areaColorMap[areaId] = color;

      console.log('更新区域颜色:', areaId, color);
      // // 发送颜色更新事件
      // BridgeEventBus.$emit(BRIDGE_EVENTS.AREA_COLOR_CHANGED, { 
      //   areaId, 
      //   color 
      // });
    }
  },
  
  // 上次触发区域选择事件的时间
  _lastAreaSelectedTime: 0,
  
  setCurrentArea(area, skipEdit = false) {
    // 如果是同一个区域对象，不重复设置
    if (state.currentArea === area) return;
    
    // 如果是相同ID的区域，通过后续的throttleEmit处理防抖
    
    // 如果当前处于绘制模式但要选择区域，先退出绘制模式
    if (state.isDrawing && area) {
      console.log('选择区域时自动退出绘制模式');
      state.isDrawing = false;
    }
    
    // 添加日志记录验证修复效果
    console.log('设置当前区域:', area ? (area.areaName || area.name) : 'null', '跳过编辑模式:', skipEdit);
    
    state.currentArea = area;
    localStorage.setItem('BridgeManagement-AreaSelection', JSON.stringify(area));
    
    // 使用throttleEmit方法发送事件，自动处理节流逻辑
    if (area) {
      // 这里需要包装payload以传递skipEdit参数
      BridgeEventBus.throttleEmit(BRIDGE_EVENTS.AREA_SELECTED, [area, skipEdit], 500);
    }
  },
  
  // 设置选中的区域（用于列表选择，不触发编辑）
  setSelectedArea(area) {
    // 防止重复设置同一个区域
    if (state.selectedArea === area) return;
    
    // 如果当前处于编辑锁定状态，需要用户确认才能切换
    if (state.isEditingLocked && state.editingAreaId && area && area.id !== state.editingAreaId) {
      console.warn('当前正在编辑区域，无法直接切换选择。请先保存或取消当前编辑。');
      return false; // 返回false表示切换失败
    }
    
    console.log('设置选中区域:', area ? (area.areaName || area.name || area.id) : 'null');
    state.selectedArea = area;
    
    // 发送区域选中事件，但不触发编辑模式
    if (area) {
      BridgeEventBus.throttleEmit(BRIDGE_EVENTS.AREA_SELECTED, [area, true], 300); // skipEdit = true
    }
    
    return true; // 返回true表示切换成功
  },
  
  // 开始编辑区域（锁定编辑状态）
  startEditingArea(area) {
    if (!area || !area.id) {
      console.warn('开始编辑区域失败：区域对象无效');
      return false;
    }
    
    // 如果要编辑的就是当前正在编辑的区域，直接返回成功
    if (state.isEditingLocked && state.editingAreaId === area.id) {
      console.log('正在编辑相同区域，无需重新开始编辑');
      return true;
    }
    
    // 如果已经在编辑其他区域，需要先结束之前的编辑
    if (state.isEditingLocked && state.editingAreaId && state.editingAreaId !== area.id) {
      console.warn('已经在编辑其他区域，自动结束之前的编辑:', state.editingAreaId);
      // 自动结束之前的编辑，而不是直接返回失败
      this.endEditingArea();
    }
    
    console.log('开始编辑区域:', area.areaName || area.name || area.id);
    
    // 设置编辑状态
    state.isEditingArea = true;
    state.editingAreaId = area.id;
    state.isEditingLocked = true;
    
    // 直接设置区域状态，不触发选择事件避免冲突
    state.currentArea = area;
    state.selectedArea = area;
    localStorage.setItem('BridgeManagement-AreaSelection', JSON.stringify(area));
    
    return true;
  },
  
  // 结束编辑区域（解除锁定）
  endEditingArea() {
    console.log('=== 结束编辑区域 ===');
    console.log('结束编辑区域ID:', state.editingAreaId);
    console.log('结束前状态:', {
      isEditingLocked: state.isEditingLocked,
      editingAreaId: state.editingAreaId,
      isEditingArea: state.isEditingArea
    });
    
    // 清除编辑状态
    state.isEditingArea = false;
    state.editingAreaId = null;
    state.isEditingLocked = false;
    
    // 清除绘制状态
    state.drawingCoordinates = [];
    state.isDrawing = false;
    
    console.log('结束后状态:', {
      isEditingLocked: state.isEditingLocked,
      editingAreaId: state.editingAreaId,
      isEditingArea: state.isEditingArea
    });
    console.log('===================');
  },
  
  // 取消编辑区域（解除锁定并恢复状态）
  cancelEditingArea() {
    console.log('取消编辑区域:', state.editingAreaId);
    
    // 清除编辑状态
    state.isEditingArea = false;
    state.editingAreaId = null;
    state.isEditingLocked = false;
    state.currentArea = null;
    
    // 清除绘制状态
    state.drawingCoordinates = [];
    state.isDrawing = false;
  },
  
  // 绘制相关 - 增强版
  // 切换绘制状态
  toggleDrawing() {
    state.isDrawing = !state.isDrawing;
    
    if (state.isDrawing) {
      console.log('区域绘制模式已启用，点击地图添加点');
      BridgeEventBus.$emit(BRIDGE_EVENTS.DRAWING_STARTED);
    } else {
      console.log('区域绘制模式已禁用');
    }
    
    return state.isDrawing;
  },
  
  // 🎯 设置标注类型
  setMarkType(markType) {
    if (state.markType !== markType) {
      state.markType = markType;
      console.log(`bridgeMapStore: 标注类型已更新为: ${state.markType}`);
    }
  },
  
  // 开始绘制
  startDrawing(active, options = {}) {
    const newState = typeof active === 'boolean' ? active : true;
    
    if (state.isDrawing === newState) return state.isDrawing; // 如果状态未改变，不执行后续操作
    
    // 设置标注类型
    if (options && options.markType) {
      state.markType = options.markType;
      console.log(`设置标注类型: ${state.markType}`);
      
      // 如果是点标注，记录图标名称
      if (state.markType === 'point' && options.iconName) {
        state.pointIconName = options.iconName;
        console.log(`设置点标注图标: ${state.pointIconName}`);
      }
    }
    
    // 如果要启动绘制模式，先清空现有坐标和当前区域
    if (newState === true) {
      console.log('启动绘制模式，先清空现有坐标');
      // 清空坐标数组和当前区域
      state.drawingCoordinates = [];
      state.currentArea = null;
      state.isEditingArea = false;
    }
    
    state.isDrawing = newState;
    
    if (state.isDrawing) {
      console.log(`区域绘制模式已启用，类型: ${state.markType}，绘制坐标已清空${state.markType === 'point' && state.pointIconName ? '，图标: ' + state.pointIconName : ''}`);
      BridgeEventBus.$emit(BRIDGE_EVENTS.DRAWING_STARTED);
    }
    
    return state.isDrawing;
  },
  
  // 结束绘制
  endDrawing() {
    if (!state.isDrawing) return;
    
    state.isDrawing = false;
    BridgeEventBus.$emit(BRIDGE_EVENTS.DRAWING_ENDED, state.drawingCoordinates);
    
    return state.drawingCoordinates;
  },
  
  // 清除绘制
  clearDrawing() {
    state.drawingCoordinates = [];
    state.isEditingArea = false;
    
    // 同时重置当前选中区域，避免坐标残留问题
    state.currentArea = null;
    
    // 发送绘制已清除事件
    BridgeEventBus.$emit(BRIDGE_EVENTS.DRAWING_CLEARED);
    
    console.log('bridgeMapStore: 绘制已清除，当前区域已重置');
  },
  
  // 清除当前区域
  clearCurrentArea() {
    // 清除绘制坐标
    state.drawingCoordinates = [];
    
    console.log('bridgeMapStore: 清除当前区域，原有坐标点数:', 
                state.drawingCoordinates ? state.drawingCoordinates.length : 0);
    
    // 获取要清除的区域信息用于事件通知
    let clearedArea = null;
    if (state.currentArea) {
      clearedArea = { ...state.currentArea };
      const areaId = clearedArea.id;
      const areaName = clearedArea.name || clearedArea.areaName;
      
      console.log(`bridgeMapStore: 正在清除区域 ID:${areaId}, 名称:${areaName}`);
      
      // 发送区域已清除事件（在重置之前发送）
      BridgeEventBus.$emit(BRIDGE_EVENTS.AREA_CLEARED, clearedArea);
      
      // 重置当前区域
      state.currentArea = null;
      
      // 保持编辑状态，让用户可以重新绘制
      state.isEditingArea = true;
      
      console.log('bridgeMapStore: 区域已清除事件已发送，当前区域已重置');
    } else {
      state.isEditingArea = false;
      console.log('bridgeMapStore: 没有当前选中的区域');
    }
    
    // 通知绘制已清除
    BridgeEventBus.$emit(BRIDGE_EVENTS.DRAWING_CLEARED);
    console.log('bridgeMapStore: 绘制清除事件已发送');
  },
  
  // 处理区域绘制点击
  handleDrawingClick(latlng) {
    // 检查参数有效性
    if (!latlng || typeof latlng.lat === 'undefined' || typeof latlng.lng === 'undefined') {
      console.warn('区域绘制：无效的坐标参数');
      return;
    }
    
    // 防止重复处理同一点位的点击
    if (latlng.lat === state.lastClickLat && latlng.lng === state.lastClickLng) {
      return;
    }
    
    // 更新最后点击的坐标
    state.lastClickLat = latlng.lat;
    state.lastClickLng = latlng.lng;
    
    // 确保当前是绘制状态
    if (!state.isDrawing) {
      console.log('当前不是绘制状态，忽略点击');
      return;
    }
    
    // 检查markType是否为点标注
    if (state.markType === 'point') {
      console.log('点标注模式：不在bridgeMapStore中处理点击，将由BridgeManagement.js中的handlePointMarkerClick方法处理');
      // 不在这里处理点标注，避免冲突
      return;
    }
    
    // 如果绘制数组非空但不是绘制状态，可能是状态不一致，先清空
    if (state.drawingCoordinates.length > 0 && !state.isDrawing) {
      console.log('检测到异常状态：非绘制模式但坐标数组非空，强制清空');
      state.drawingCoordinates = [];
      state.currentArea = null;
      state.isEditingArea = false;
    }
    
    console.log('当前区域多边形点数:', state.drawingCoordinates.length);
    console.log(`添加坐标点到绘制数组(${state.markType}):`, [latlng.lat, latlng.lng]);
    
    // 根据不同的标注类型处理点击事件
    switch (state.markType) {
      case 'point':
        // 点标注：直接添加点，然后结束绘制
        state.drawingCoordinates = [[latlng.lat, latlng.lng]];
        state.isDrawing = false;
        state.isEditingArea = true;
        // 发送点标注事件，包含图标信息
        BridgeEventBus.$emit(BRIDGE_EVENTS.DRAWING_ENDED, {
          coordinates: state.drawingCoordinates,
          iconName: state.pointIconName || 'el-icon-location'
        });
        console.log(`点标注已完成，坐标: [${latlng.lat}, ${latlng.lng}], 图标: ${state.pointIconName || 'el-icon-location'}`);
        return;
      
      case 'line':
        // 线段标注：添加起点和终点，超过2个点后自动结束
        this.addDrawingPoint(latlng);
        if (state.drawingCoordinates.length >= 2) {
          // 线段只需要2个点，达到后自动结束绘制
          state.isDrawing = false;
          state.isEditingArea = true;
          BridgeEventBus.$emit(BRIDGE_EVENTS.DRAWING_ENDED, state.drawingCoordinates);
        }
        return;
      
      case 'circle':
        // 圆形标注：添加中心点和边界点，超过2个点后自动结束
        this.addDrawingPoint(latlng);
        if (state.drawingCoordinates.length >= 2) {
          // 圆形只需要2个点（中心点+边界点），达到后自动结束绘制
          state.isDrawing = false;
          state.isEditingArea = true;
          BridgeEventBus.$emit(BRIDGE_EVENTS.DRAWING_ENDED, state.drawingCoordinates);
          console.log(`圆形绘制完成，坐标数量: ${state.drawingCoordinates.length}`);
        }
        return;
      
      case 'rectangle':
        // 矩形标注：添加两个点（对角），然后自动计算其他两个点
        this.addDrawingPoint(latlng);
        if (state.drawingCoordinates.length === 2) {
          // 获取已有的两个点（对角点）
          const point1 = state.drawingCoordinates[0]; // 第一个点
          const point2 = state.drawingCoordinates[1]; // 对角点
          
          // 🎯 重新计算矩形的4个顶点，确保顺序正确
          // 根据两个对角点计算完整矩形
          const minLat = Math.min(point1[0], point2[0]);  // 最小纬度
          const maxLat = Math.max(point1[0], point2[0]);  // 最大纬度  
          const minLng = Math.min(point1[1], point2[1]);  // 最小经度
          const maxLng = Math.max(point1[1], point2[1]);  // 最大经度
          
          // 按逆时针顺序定义矩形的4个顶点
          const topLeft = [maxLat, minLng];     // 左上角
          const topRight = [maxLat, maxLng];    // 右上角
          const bottomRight = [minLat, maxLng]; // 右下角
          const bottomLeft = [minLat, minLng];  // 左下角
          
          // 按逆时针顺序排列顶点，形成完整矩形
          state.drawingCoordinates = [
            topLeft,      // 0: 左上
            topRight,     // 1: 右上
            bottomRight,  // 2: 右下
            bottomLeft    // 3: 左下
          ];
          
          console.log('矩形坐标已生成:', state.drawingCoordinates);
          
          // 结束绘制
          state.isDrawing = false;
          state.isEditingArea = true;
          BridgeEventBus.$emit(BRIDGE_EVENTS.DRAWING_ENDED, state.drawingCoordinates);
        }
        return;
      

      
      case 'polygon':
      default:
        // 多边形标注：处理原有逻辑
        // 检查是否已经有坐标点，并且点击了第一个点，表示闭合多边形
        if (state.drawingCoordinates.length > 2) {
          const firstPoint = state.drawingCoordinates[0];
          // 计算点击位置与第一个点的距离
          const distance = mapUtils.calculateDistance(
            latlng.lat, latlng.lng, 
            firstPoint[0], firstPoint[1]
          );
          
          // 如果点击位置接近第一个点，自动闭合多边形
          if (distance < 0.0001) { // 约10米左右，可根据需要调整
            // 闭合多边形不需要添加新点，自动停止绘制
            state.isDrawing = false;
            
            // 设置编辑状态，确保后续能够保存
            state.isEditingArea = true;
            
            // 发送绘制完成事件
            BridgeEventBus.$emit(BRIDGE_EVENTS.DRAWING_ENDED, state.drawingCoordinates);
            
            return;
          }
        }
        
        // 添加点到绘制坐标数组
        this.addDrawingPoint(latlng);
        
        // 确保编辑状态被设置
        if (state.drawingCoordinates.length >= 3) {
          state.isEditingArea = true;
        }
    }
  },
  
  // 添加绘制点
  addDrawingPoint(latlng) {
    if (latlng && typeof latlng.lat !== 'undefined' && typeof latlng.lng !== 'undefined') {
      state.drawingCoordinates.push([latlng.lat, latlng.lng]);
    }
  },
  
  // 计算两点间的距离 - 使用mapUtils中的通用方法
  calculateDistance(lat1, lng1, lat2, lng2) {
    return mapUtils.calculateDistance(lat1, lng1, lat2, lng2);
  },
  
  // UI相关
  showDrawer() {
    state.drawerVisible = true;
  },
  
  hideDrawer() {
    state.drawerVisible = false;
  },
  
  setLoading(isLoading) {
    state.loading = isLoading;
  },
  
  // 坐标拾取相关 - 增强版
  // 切换位置拾取模式
  toggleLocationPicking() {
    state.isLocationPicking = !state.isLocationPicking;
    
    if (state.isLocationPicking) {
      console.log('位置拾取模式已启用，请点击地图选择位置');
      BridgeEventBus.$emit(BRIDGE_EVENTS.LOCATION_PICKING_STARTED);
    } else {
      console.log('位置拾取模式已禁用');
    }
    
    return state.isLocationPicking;
  },
  
  // 启动位置拾取模式
  startLocationPicking() {
    state.isLocationPicking = true;
    console.log('位置拾取模式已启用，请点击地图选择位置');
    BridgeEventBus.$emit(BRIDGE_EVENTS.LOCATION_PICKING_STARTED);
  },
  
  // 结束位置拾取模式并处理选择的位置
  endLocationPicking(latlng) {
    // 检查参数有效性
    if (!latlng || typeof latlng.lat === 'undefined' || typeof latlng.lng === 'undefined') {
      console.warn('位置拾取：无效的坐标参数');
      state.isLocationPicking = false;
      return;
    }
    
    try {
      // 更新当前标记位置
      state.currentMarker = [latlng.lat, latlng.lng];
      
      // 如果有当前桥梁对象，更新其经纬度
      if (state.currentBridge) {
        state.currentBridge.latitude = latlng.lat;
        state.currentBridge.longitude = latlng.lng;
        
        // 如果桥梁有ID，缓存位置
        if (state.currentBridge.id) {
          this.cacheBridgePosition(state.currentBridge);
        }
      }
      
      // 发送位置拾取完成事件
      BridgeEventBus.$emit(BRIDGE_EVENTS.LOCATION_PICKED, latlng);
      
      // 关闭拾取模式
      state.isLocationPicking = false;
      
      console.log('位置拾取完成:', latlng);
    } catch (error) {
      console.error('处理位置拾取结果出错:', error);
      state.isLocationPicking = false;
    }
  },
  
  // 设置当前标记位置，不涉及拾取模式
  setMarkerPosition(latLng) {
    if (latLng && typeof latLng.lat !== 'undefined' && typeof latLng.lng !== 'undefined') {
      state.currentMarker = [latLng.lat, latLng.lng];
    } else if (Array.isArray(latLng) && latLng.length >= 2) {
      state.currentMarker = [latLng[0], latLng[1]];
    }
  },
  
  // 新增：缓存桥梁位置信息到localStorage
  cacheBridgePosition(bridge) {
    if (!bridge || !bridge.id || !bridge.latitude || !bridge.longitude) {
      console.warn('缓存桥梁位置失败：参数不完整');
      return;
    }
    
    try {
      // 只缓存必要的位置信息，减少存储空间占用
      const positionData = {
        id: bridge.id,
        bridgeName: bridge.bridgeName,
        latitude: bridge.latitude,
        longitude: bridge.longitude,
        timestamp: Date.now()
      };
      
      // 使用桥梁ID作为key，方便根据ID快速查找
      localStorage.setItem(`bridge_position_${bridge.id}`, JSON.stringify(positionData));
      console.log(`已缓存桥梁[${bridge.id}]位置信息`);
    } catch (error) {
      console.warn('缓存桥梁位置信息失败:', error);
    }
  },
  
  // 新增：从localStorage获取桥梁位置信息
  getBridgePositionFromCache(bridgeId) {
    if (!bridgeId) return null;
    
    try {
      const positionCache = localStorage.getItem(`bridge_position_${bridgeId}`);
      if (!positionCache) return null;
      
      const positionData = JSON.parse(positionCache);
      // 验证数据有效性
      if (positionData && positionData.latitude && positionData.longitude) {
        console.log(`已从缓存获取桥梁[${bridgeId}]位置信息`);
        return positionData;
      }
    } catch (error) {
      console.warn(`获取桥梁[${bridgeId}]缓存位置失败:`, error);
    }
    
    return null;
  }
};

// 监听地图点击事件，处理绘制和拾取坐标
BridgeEventBus.$on(BRIDGE_EVENTS.MAP_CLICK, (latlng) => {
  if (state.isLocationPicking) {
    actions.endLocationPicking(latlng);
  } else if (state.isDrawing) {
    actions.handleDrawingClick(latlng);
  }
});

// 添加重置所有状态的方法
actions.resetAllState = function() {
  console.log('重置桥梁管理页面所有状态...');
  
  // 保存地图实例，因为它不应该被重置
  const mapInstance = state.map;
  
  // 重置所有状态为初始值
  Object.keys(initialState).forEach(key => {
    // 除了map实例外，所有状态都重置为初始值
    if (key === 'map') return;
    
    // 复制一份初始值，避免引用问题
    const initialValue = Array.isArray(initialState[key]) 
      ? [...initialState[key]] 
      : (typeof initialState[key] === 'object' && initialState[key] !== null)
        ? {...initialState[key]}
        : initialState[key];
    
    state[key] = initialValue;
  });
  
  // 恢复地图实例
  state.map = mapInstance;
  
  // 发送状态重置事件
  BridgeEventBus.$emit(BRIDGE_EVENTS.STATE_RESET);
  
  console.log('桥梁管理页面状态已重置');
};

// 添加强制清理编辑锁定状态的方法
actions.forceUnlockEditing = function() {
  console.log('强制清理编辑锁定状态...');
  console.log('清理前状态:', {
    isEditingLocked: state.isEditingLocked,
    editingAreaId: state.editingAreaId,
    isEditingArea: state.isEditingArea
  });
  
  // 强制清除编辑状态
  state.isEditingArea = false;
  state.editingAreaId = null;
  state.isEditingLocked = false;
  
  // 清除绘制状态
  state.drawingCoordinates = [];
  state.isDrawing = false;
  
  console.log('强制清理完成，当前状态:', {
    isEditingLocked: state.isEditingLocked,
    editingAreaId: state.editingAreaId,
    isEditingArea: state.isEditingArea
  });
};

export default {
  state,
  getters,
  actions
}; 